import React, { Component } from 'react';
import PageHeader from './PageHeader';
import HorizontalBars from '../charts/HorizontalBars';
import RowTitle from '../charts/RowTitle';
import Reflection from './Reflection';
import { average } from '../../helpers';
import ComparisonKey from './ComparisonKey';
import ReviewerTypeKeys from './ReviewerTypeKeys';

export default class OrderedStatements extends Component {
  constructor(props) {
    super(props);

    this.state = {
      limits: {
        'Highest': [{lowerLimit: 0, upperLimit: 5}],
        'Lowest': [{lowerLimit: 0, upperLimit: 5}]
      }
    };
  }

  componentDidMount() {
    const { reportType, reviewerAnswersAverages } = this.props;
    let reviewersAnswerScores = {};
    if (reportType === 'User') {
      for (const questionID in reviewerAnswersAverages) {
        reviewersAnswerScores[questionID] = +reviewerAnswersAverages[questionID][0];
      }
    } else if (reportType === 'Comparison') {
      for (const questionID in reviewerAnswersAverages) {
        reviewersAnswerScores[questionID] = +reviewerAnswersAverages[questionID];
      }
    } else {
      reviewersAnswerScores = reviewerAnswersAverages;
    }
    const answerKeys = Object.keys(reviewersAnswerScores);
    const sortedAnswerKeys = answerKeys.sort((a,b) => reviewersAnswerScores[b] - reviewersAnswerScores[a])
    this.setState({ sortedAnswerKeys, reviewersAnswerScores });
  }

  componentDidUpdate(prevProps, prevState) {
    // when pdf export is requested, an initial re-render alters the page width to match the pdf, then calculates page break limits and re-renders back to normal width
    if (prevProps.layoutMode !== this.props.layoutMode) {
      if (this.props.layoutMode === 'pdfRecalculate') {
        const { sortedAnswerKeys } = this.state;
        let limits = {
          'Highest': [],
          'Lowest': []
        };
        const updateLimits = (orderType) => {
          const answerKeys = orderType === 'Highest' ? [...sortedAnswerKeys] : [...sortedAnswerKeys].reverse()
          let count = 0;
          let lowerLimit = 0;
          let upperLimit;
          // accounts for intro text on first page
          let spaceAvailable = orderType === 'Highest' ? 400 : 460;
          answerKeys.forEach((questionID, i) => {
            if (i < 5) {
              // get row height and check if it will fit, if not update limits and reset count
              let rowHeight = 0;
              const chartRow = this[`statementRow${orderType}-${questionID}`];
              if (chartRow) {
                rowHeight = chartRow.offsetHeight + 1;
              }
              if ((count + rowHeight) < spaceAvailable) {
                count += rowHeight;
              } else {
                upperLimit = i;
                limits[orderType].push({lowerLimit, upperLimit});
                lowerLimit = i;
                count = rowHeight;
                spaceAvailable = 460;
              }
            }
          })
          limits[orderType].push({lowerLimit, upperLimit: 5});
        }
        updateLimits('Highest')
        updateLimits('Lowest')
        this.setState({ limits });
        this.props.updateOrderedStatementsPDFExportPreRenderStatus();
      }
    }
  }

  render() {
    const { reportType, generateGraph, questionDetails, userIndividualAnswers, previousReviewerAverages, previousRevieweeAverages, layoutMode } = this.props;
    const { limits, sortedAnswerKeys, reviewersAnswerScores } = this.state;
    const userColour = '#0051ff';
    const reviewerColour = '#F563B9';
    let reviewerTypes = reportType === 'Team' ? ['Team Members'] : ['Reviewers', 'Self'];
    if (reportType === 'Comparison') {
      reviewerTypes.splice(1,0,'space');
    }
    const orderTypes = ['Highest', 'Lowest'];
    const introText = () => {
      if (reportType === 'Team') {
        return [
          "This chart shows the teams highest and lowest ranked statements, based on ratings from team members.",
          "This will show you the specific statements that have been observed by the team members as strongest and the least strong. These should generally align with the capability rankings and can be explored in more detail in 'Team detailed feedback'."
        ]
      } else if (reportType === 'Group') {
        return  [
          "This chart shows the group’s highest ranked statements, and its lowest ranked statements, based on the feedback received. You can compare each rating against the collective self-review for the same statement."
        ]
      } else {
        return  [
          "This chart shows your highest ranked statements, and your lowest ranked statements, based on your colleagues' ratings. You can also compare each rating against your self-review for the same statement.",
          "This will show you the specific statements that have been observed by your colleagues as strongest and the least strong. These should generally align with the capability rankings and can be explored in more detail in 'Your detailed feedback'."
        ]
      }
    }
    return (generateGraph && limits && sortedAnswerKeys) ? (
      <>
        {orderTypes.map(orderType => {
          const orderedAnswerKeys = orderType === 'Highest' ? [...sortedAnswerKeys] : [...sortedAnswerKeys].reverse();
          return (
            limits[orderType].map((limitsObj, i) => {
              const { lowerLimit, upperLimit } = limitsObj;
              return (
                <div key={orderType + i} className='bg-white page-break user-report__content'>
                  <PageHeader
                    h2Class='mt4'
                    h2Text='Part 1: '
                    strong='Step 3'
                    h3Text='Statements: ranked highest and lowest'
                    line={true}
                  />

                  {(orderType === 'Highest' && i === 0) && introText().map((text, introTextIndex) => {
                    return <p key={'intro-text' + introTextIndex}>{text}</p>
                  })}

                  <div 
                    className='bb b--light-gray mv5'
                    style={{ width: layoutMode === 'pdfRecalculate' ? '158mm' : ''}}
                  >
                    <p>
                      <strong>{orderType} Average Statements{i > 0 ? ' (continued)' : ''}</strong>
                    </p>

                    {(reportType === 'Comparison' && orderType === 'Highest' && i === 0) && <ComparisonKey />}

                    {orderedAnswerKeys && (
                      orderedAnswerKeys.map((key, answerIndex) => {
                        let previousReviewerAnswer;
                        if (reportType !== 'Self') {
                          if (reportType === 'Comparison') {
                            previousReviewerAnswer = average(previousReviewerAverages[key])
                          }
                        }
                        return reviewersAnswerScores[key] > 0 && (
                          (answerIndex >= lowerLimit && answerIndex < upperLimit) && (
                            <div 
                              key={key}
                              ref={(questionRow) => {
                                this[`statementRow${orderType}-${key}`] = questionRow
                              }}
                              className='w-100 flex bt b--light-gray'
                            >
                              <RowTitle containerClass='w-40 pr2' title={questionDetails[key].questionTitle} />
                      
                              {reportType !== 'Self' && (
                                <ReviewerTypeKeys reviewerTypes={reviewerTypes} userColour={userColour} reviewerColour={reviewerColour} />
                              )}
                              
                              <HorizontalBars 
                                reportType={reportType} 
                                containerClass='w-40' 
                                pClass='flex' 
                                selfAnswerScore={userIndividualAnswers && userIndividualAnswers[key].toFixed(1)}
                                previousRevieweeAverages={previousRevieweeAverages && previousRevieweeAverages[key].toFixed(1)}
                                reviewersAnswerScore={reviewersAnswerScores && reviewersAnswerScores[key].toFixed(1)}
                                previousReviewerAverages={previousReviewerAnswer && previousReviewerAnswer.toFixed(1)}
                                userColour={userColour} 
                                reviewerColour={reviewerColour} 
                              />
                            </div>
                          )
                        )
                      })
                    )}
                  </div>
                </div>
              )
            })
          )
        })}
        {((reportType !== 'Team') && (reportType !== 'Group')) && (
          <Reflection step='3' subTitle='Statements: ranked highest and lowest' />
        )}
      </>   
    ) : (
      <></>
    )
  }
}
